import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchStopLossStatus } from "../../../utils/apiUtils";

const COOLDOWN_MS = 10 * 60 * 1000;
const CLOCK_TICK_MS = 30 * 1000;

function useNow(intervalMs) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
}

export function useStopLossStatus() {
    // re-render periodically so relative-time and cooldown text stay
    // current between manual refreshes — this does not fetch anything
    useNow(CLOCK_TICK_MS);

    const { data, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey: ["stoploss-status"],
        queryFn: fetchStopLossStatus,
        retry: false,
    });

    const cooldownRemainingMs = useMemo(() => {
        if (!data?.lastStoppedAt) return 0;
        const elapsed = Date.now() - new Date(data.lastStoppedAt).getTime();
        return Math.max(0, COOLDOWN_MS - elapsed);
    }, [data?.lastStoppedAt]);

    const state = data?.state;
    const lastExitCode = data?.lastExitCode;
    const failed = state !== "running" && typeof lastExitCode === "number" && lastExitCode !== 0;
    const inCooldown = state !== "running" && cooldownRemainingMs > 0;
    const disabled = isLoading || isError || state === "running" || inCooldown;

    return {
        data,
        isLoading,
        isFetching,
        isError,
        refetch,
        state,
        failed,
        inCooldown,
        disabled,
        cooldownRemainingMs,
    };
}

export function relativeTime(iso) {
    if (!iso) return null;
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins === 1) return "1 minute ago";
    if (mins < 60) return `${mins} minutes ago`;
    const hours = Math.round(mins / 60);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
}
